import { ApolloQueryResult } from '@apollo/client';
import { IProps } from '@components/messages/types';
import React, { useEffect, useState } from 'react';
import { ITableActions } from 'apps/things/app/item/context/ItemProvider';

import { GetAllBrokersResponse_page_edges } from '../types/GetAllBrokersResponse';
import { GetAllBrokers, GetAllBrokersVariables } from '../types/GetAllBrokers';
import { useLazyGetAllBrokerQuery } from '../queries';
import useOVSPagination from 'hooks/useOVSPagination';
import { funNumberAgr } from '@types';
export interface IBrokerContext {
  items: GetAllBrokersResponse_page_edges[];
  setItems: (item: GetAllBrokersResponse_page_edges[]) => void;
  loading: boolean;
  getItems: () => void;
  activeStage: number;
  setActiveStage: (stage: number) => void;
  tableActions: ITableActions[];
  refetchItems:
    | ((
        variables?: Partial<GetAllBrokersVariables> | undefined
      ) => Promise<ApolloQueryResult<GetAllBrokers>>)
    | undefined;
  setPageLimit: funNumberAgr;
  paging: any;
  goTo: (nextPrev: boolean) => void;
}

export const BrokerContext = React.createContext<IBrokerContext>(
  {} as IBrokerContext
);

const BrokerProvider: React.FC<IProps> = ({ children }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [item, setItem] = React.useState(
    {} as GetAllBrokersResponse_page_edges
  );
  const [items, setItems] = React.useState<GetAllBrokersResponse_page_edges[]>(
    []
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const {
    endCursorStack,
    initPagAction,
    setPaging,
    paging,
    setPageListLimit,
    pageListLimit
  } = useOVSPagination();
  /// hooks
  const [
    getItems,
    { refetch: refetchItems, data, loading, fetchMore },
  ] = useLazyGetAllBrokerQuery({
    first: pageListLimit,
  });

  useEffect(() => {
    setItems(data?.getAllBrokers?.page?.edges || []); 
    setPaging({
      ...data?.getAllBrokers?.pageData,
      ...data?.getAllBrokers?.page?.pageInfo,
    }); // eslint-disable-next-line
  }, [data]);

  const goTo = async (next = true) => {
    if (isLoadingMore) return;

    let variables: GetAllBrokersVariables = {
      first: pageListLimit,
      ...initPagAction(next),
    };

    if (fetchMore) {
      setIsLoadingMore(true);
      const _data = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllBrokers?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllBrokers?.pageData,
        ..._data?.data?.getAllBrokers?.page?.pageInfo,
        hasPreviousPage: endCursorStack.length > 0,
      });
    }
  };

  const setPageLimit = (limit: number) => {
    setPageListLimit(limit)
    setTimeout(() => {
      refetchItems && refetchItems()
    }, 100);

  }

  const value = React.useMemo(
    () => ({
      item,
      setItem,
      items,
      setItems,
      loading: loading,
      getItems: getItems,
      refetchItems,
      tableActions: [],
      activeStage,
      setActiveStage,
      setPageLimit,
      goTo,
      paging
    }), // eslint-disable-next-line
    [items, loading]
  );

  return (
    <BrokerContext.Provider value={value}>{children}</BrokerContext.Provider>
  );
};

export default BrokerProvider;
